import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import { useEliminarTramiteMutation, useGetEtapasTramiteQuery } from "../services/docs";
import { cambiarEtapa } from "../features/ui/uiSlice";
import { RootState } from "../store";
import { elapsed, elapsedCerrado, fecha_actual } from "../hooks/elapsed";
import { Spinner } from "../components/Spinner";
import { useForm } from "../hooks/useForm";

export const TramiteLayout = () => {
  const path = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const url = path.pathname.split("/");
  const tab = url[url.length - 1];
  const tabs = ["expediente", "contacto-cliente", "tiempos", "dictamenes"];

  const { siniestro, tramite_id } = useParams();

  const { data: etapa, isLoading } = useGetEtapasTramiteQuery({
    tramite: tramite_id,
    siniestro: siniestro,
  });

  const { etapaActual, fc_sub_etapa } = useSelector((state: RootState) => state.ui);

  const [eliminarTramiteAction, { isLoading: loadingEliminar }] = useEliminarTramiteMutation();

  const [elimina, setElimina] = useState(false);

  const { formulario, handleInputChange, handleSelectChange } = useForm({
    fi_rechazo: 0,
    fc_motivo_rechazo: "",
    fi_tramite_id: +(tramite_id || 0),
  });

  // Efecto para sincronizar la etapa actual con Redux
  useEffect(() => {
    if (etapa?.tramite?.t_fi_etapa && etapaActual === 0) {
      dispatch(
        cambiarEtapa({
          etapaActual: etapa.tramite.t_fi_etapa,
          fc_sub_etapa: etapa.tramite.fc_sub_etapa,
        })
      );
    }
  }, [etapa, etapaActual, dispatch]);

  const eliminarTramite = async () => {
    if (+formulario.fi_rechazo === 0) {
      toastr.error("Debe seleccionar un motivo de rechazo/eliminación");
      return;
    }
    if (formulario.fc_motivo_rechazo.trim() === "") {
      toastr.error("Debe escribir una descripción del motivo");
      return;
    }

    try {
      const resp = await eliminarTramiteAction({
        ...formulario,
        fi_rechazo: +formulario.fi_rechazo,
        fi_tramite_id: +formulario.fi_tramite_id,
      }).unwrap();

      if (resp) {
        toastr.warning(resp.message || "Trámite eliminado/rechazado");
        navigate(`/home/mis-pendientes`); // O redirección deseada
      }
    } catch (error) {
      toastr.error("Ocurrió un error al intentar eliminar el trámite");
    }
  };

  // Helper para renderizar el nombre del último analista
  const renderAnalista = (t: any) => {
    if (t.eje_id) {
      return `${t.eje_fc_nombre} ${t.eje_fc_apellido_paterno}`;
    }
    if (t.sso_id) {
      return `${t.sso_fc_name}`;
    }
    return "Sin Asignar";
  };

  if (isLoading || !etapa?.tramite) {
    return (
      <div className="flex h-64 w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const t = etapa.tramite;

  return (
    <div className="space-y-5">

      {/* --- BANNER DOSSIER (Información del Caso) --- */}
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        {/* Cabecera del Dossier */}
        <div className="flex flex-col gap-4 border-b border-slate-100 pb-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-brand-50 p-2.5 text-brand-600">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-blue-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-brand-700">
                  {t.s_fc_descripcion_aseguradora || "Aseguradora"}
                </span>
                <span className="font-mono text-xs text-slate-400">
                  Folio: <strong className="font-bold text-slate-800">{t.s_fc_siniestro}</strong>
                </span>
                <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                  <span className="mr-1 h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                  En Trámite
                </span>
              </div>
              <h1 className="mt-0.5 text-lg font-bold text-slate-900">
                {t.t_fi_producto_id} - {t.s_fc_descripcion_producto} | {t.t_fi_cobertura_id} - {t.s_fc_descripcion_cobertura}
              </h1>
            </div>
          </div>

          {/* Botones de Acción */}
          <div className="flex items-center gap-2 self-end md:self-auto">
            <button
              onClick={() => setElimina(!elimina)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-600 transition-colors hover:bg-rose-50"
            >
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
              {elimina ? "Cancelar" : "Eliminar Trámite"}
            </button>
          </div>
        </div>

        {/* Formulario de Eliminación (Desplegable) */}
        {elimina && (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50/50 p-4">
            <h3 className="mb-3 text-sm font-bold text-rose-800 flex items-center gap-2">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
              Motivo de Cancelación / Rechazo
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
              <div className="md:col-span-4">
                <select
                  name="fi_rechazo"
                  onChange={handleSelectChange}
                  className="h-11 w-full rounded-xl border border-rose-200 bg-white px-3 text-sm font-medium text-slate-800 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20"
                >
                  <option value="">Selecciona el motivo...</option>
                  {etapa?.rechazos?.map((b: any) => (
                    <option key={b.id} value={b.id}>{b.fc_sub_etapa}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-6">
                <input
                  type="text"
                  name="fc_motivo_rechazo"
                  placeholder="Describe brevemente el motivo..."
                  onChange={handleInputChange}
                  className="h-11 w-full rounded-xl border border-rose-200 bg-white px-3 text-sm font-medium text-slate-800 placeholder:text-rose-300 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20"
                />
              </div>
              <div className="md:col-span-2 flex items-end">
                <button
                  onClick={eliminarTramite}
                  disabled={loadingEliminar}
                  className="flex h-11 w-full items-center justify-center rounded-xl bg-rose-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 disabled:opacity-50"
                >
                  {loadingEliminar ? <Spinner /> : "Confirmar"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Metadata Grid */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 pt-4 text-xs md:grid-cols-4 lg:grid-cols-5">
          <div>
            <span className="block font-medium text-slate-400">Asegurado Titular</span>
            <span className="text-sm font-semibold text-slate-900">{t.t_fc_nombre_asegurado}</span>
            <span className="block font-mono text-[11px] text-slate-500">Póliza: {t.s_fc_poliza_aseguradora}</span>
          </div>
          <div>
            <span className="block font-medium text-slate-400">Monto Reclamado</span>
            <span className="text-sm font-bold text-slate-900">{t.t_fn_monto_reclamado} {t.s_fc_iso_moneda}</span>
            <span className="block text-[11px] text-slate-500">Contacto: {t.s_fc_correo_contacto || "N/A"}</span>
          </div>
          <div>
            <span className="block font-medium text-slate-400">Fechas del Evento</span>
            <div className="text-slate-700">
              Siniestro: <strong className="font-medium text-slate-900">{fecha_actual(t.t_fd_fecha_siniestro).replace("0:0:0", "")}</strong>
            </div>
            <div className="text-[11px] text-slate-500">
              Registro: {fecha_actual(t.t_fd_registro_tramite)}
            </div>
          </div>
          <div>
            <span className="block font-medium text-slate-400">Último Analista</span>
            <span className="font-medium text-slate-900">{renderAnalista(t)}</span>
            {t.sso_id && <span className="mt-0.5 inline-block rounded bg-brand-50 px-1.5 py-0.5 text-[11px] text-brand-600">{t.sso_fc_origen}</span>}
          </div>
          <div className="col-span-2 border-t border-slate-100 pt-3 md:col-span-1 md:border-l md:border-t-0 md:pl-4 md:pt-0">
            <span className="block font-medium text-slate-400">Tiempos del Trámite</span>
            <div className="mt-0.5 flex items-center gap-1.5 font-semibold text-amber-600">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
              <span>{t.t_fi_etapa === 7 ? elapsedCerrado(t.t_fd_registro_tramite, t.sem_fd_fecha_inicial) : elapsed(t.sem_fd_fecha_inicial)}</span>
            </div>
            <span className="block text-[10px] text-slate-400">Total: {elapsed(t.t_fd_registro_tramite)}</span>
          </div>
        </div>
      </section>

      {/* --- WORKFLOW STEPPER --- */}
      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 px-1 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500">
            <span>Flujo del Proceso</span>
            <span className="text-slate-300">•</span>
            <span className="text-brand-600">{fc_sub_etapa}</span>
          </h2>
        </div>

        <div className="flex flex-col gap-2 md:flex-row">
          {etapa?.etapas?.map((e: any, index: number) => {
            const isCompleted = e.fi_etapa < etapaActual;
            const isActive = e.fi_etapa === etapaActual;

            return (
              <div
                key={e.fi_etapa}
                className={`relative flex-1 rounded-lg p-2.5 text-center transition-all ${isCompleted ? "bg-emerald-50 border border-emerald-200" :
                    isActive ? "bg-brand-600 text-white shadow-md ring-2 ring-brand-500 ring-offset-2" :
                      "bg-slate-50 border border-slate-200 opacity-70"
                  }`}
              >
                <div className={`mx-auto mb-1 flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${isCompleted ? "bg-emerald-500 text-white" :
                    isActive ? "bg-white text-brand-600" :
                      "bg-slate-200 text-slate-600"
                  }`}>
                  {isCompleted ? <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg> : (index + 1)}
                </div>
                <div className={`text-[11px] tracking-tight ${isActive ? "font-bold" : isCompleted ? "font-semibold text-emerald-800" : "font-medium text-slate-700"}`}>
                  {e.fc_nombre_etapa}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* --- NAVIGATION TABS --- */}
      <section className="space-y-4">
        <div className="flex flex-col gap-3 border-b border-slate-200 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center space-x-6 overflow-x-auto custom-scrollbar">

            <Link to={`/view/siniestro/${siniestro}/${tramite_id}/proceso`}
              className={`flex items-center gap-2 px-1 py-2.5 text-xs transition-colors whitespace-nowrap ${!tabs.includes(tab)
                  ? "border-b-2 border-brand-600 font-bold text-brand-600"
                  : "font-medium text-slate-500 hover:text-slate-800"
                }`}
            >
              Proceso Actual
            </Link>

            <Link to={`/view/siniestro/${siniestro}/${tramite_id}/expediente`}
              className={`flex items-center gap-2 px-1 py-2.5 text-xs transition-colors whitespace-nowrap ${tab === tabs[0]
                  ? "border-b-2 border-brand-600 font-bold text-brand-600"
                  : "font-medium text-slate-500 hover:text-slate-800"
                }`}
            >
              Expediente Digital
            </Link>

            <Link to={`/view/siniestro/${siniestro}/${tramite_id}/contacto-cliente`}
              className={`flex items-center gap-2 px-1 py-2.5 text-xs transition-colors whitespace-nowrap ${tab === tabs[1]
                  ? "border-b-2 border-brand-600 font-bold text-brand-600"
                  : "font-medium text-slate-500 hover:text-slate-800"
                }`}
            >
              Contacto Cliente
            </Link>

            <Link to={`/view/siniestro/${siniestro}/${tramite_id}/tiempos`}
              className={`flex items-center gap-2 px-1 py-2.5 text-xs transition-colors whitespace-nowrap ${tab === tabs[2]
                  ? "border-b-2 border-brand-600 font-bold text-brand-600"
                  : "font-medium text-slate-500 hover:text-slate-800"
                }`}
            >
              Tiempos & SLA
            </Link>

            <Link to={`/view/siniestro/${siniestro}/${tramite_id}/dictamenes`}
              className={`flex items-center gap-2 px-1 py-2.5 text-xs transition-colors whitespace-nowrap ${tab === tabs[3]
                  ? "border-b-2 border-brand-600 font-bold text-brand-600"
                  : "font-medium text-slate-500 hover:text-slate-800"
                }`}
            >
              Dictámenes
            </Link>
          </div>
        </div>

        {/* --- OUTLET (Contenido de cada pestaña) --- */}
        <div className="pt-2">
          <Outlet />
        </div>
      </section>

    </div>
  );
};
